// Shared TERVIEW PDF branding — clean, editorial, professional.
//
// Design intent: a piece of work product, not a marketing handout.
// White pages, generous whitespace, hairline rules. Typography does
// the work (weight + size + tracking). One restrained accent color,
// used only for the brand mark and the Q-number / eyebrow labels.
import { jsPDF } from 'jspdf';

// A4 portrait, mm units. CW = content width inside the side margins.
export const PDF = { W: 210, H: 297, M: 22, CW: 166 };

// Brand color tokens — used consistently across cover, headers,
// footers and the Q/A blocks so the document reads as one design.
export const COLOR = {
  ink:      [22, 22, 28],    // primary text — near-black with hint of indigo
  text:     [55, 55, 68],    // body copy
  muted:    [115, 115, 130], // captions, eyebrows, footers
  hairline: [225, 225, 232], // 0.3pt rule color
  accent:   [109, 40, 217],  // single brand accent — purple
};

export const createDoc = () => new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

export const fR = (doc, r, g, b, x, y, w, h) => {
  doc.setFillColor(r, g, b); doc.rect(x, y, w, h, 'F');
};
export const sR = (doc, r, g, b, lw, x, y, w, h) => {
  doc.setDrawColor(r, g, b); doc.setLineWidth(lw); doc.rect(x, y, w, h, 'S');
};
export const T = (doc, color, size, bold, text, x, y, opts) => {
  doc.setTextColor(...color); doc.setFontSize(size);
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  // CRITICAL: pass arrays through unchanged so multi-line text renders
  // properly (one entry per line). Stringifying an array joins with
  // commas and collapses everything to one line.
  doc.text(Array.isArray(text) ? text : String(text), x, y, opts || {});
};
export const hLine = (doc, x1, y, x2, r, g, b, lw = 0.3) => {
  doc.setDrawColor(r, g, b); doc.setLineWidth(lw); doc.line(x1, y, x2, y);
};

// ── Cover page (page 1) ────────────────────────────────────────────────
// Clean editorial cover. Brand mark at top-left, "terview.ai" at top-right,
// hairline rule below. Role name as the document headline, company line
// below it, date right-aligned. No filled blocks, no accent stripes that
// could overlap text.
export const drawCoverHeader = (doc, { reportTitle, role, company, dateStr }) => {
  const { W, M } = PDF;

  // Top brand row
  T(doc, COLOR.ink,   12, true,  'TERVIEW',  M, 19);
  T(doc, COLOR.muted, 7.5, false, 'terview.ai', W - M, 19, { align: 'right' });

  // Letter-spaced micro caption under the wordmark
  doc.setCharSpace(0.8);
  T(doc, COLOR.muted, 6.5, false, 'INTERVIEW PREPARATION REPORT', M, 24);
  doc.setCharSpace(0);

  // Hairline rule
  hLine(doc, M, 30, W - M, COLOR.hairline[0], COLOR.hairline[1], COLOR.hairline[2], 0.3);

  // Big editorial title block — sits ~58mm down so there's breathing
  // room from the rule. Eyebrow ("Interview Questions"), then a large
  // bold role name as the document's actual headline, then the
  // supporting company line.
  doc.setCharSpace(1.4);
  T(doc, COLOR.muted, 7, true, (reportTitle || 'Interview Questions').toUpperCase(), M, 56);
  doc.setCharSpace(0);

  T(doc, COLOR.ink, 26, true, role || 'Interview Questions', M, 73);

  if (company) {
    T(doc, COLOR.text, 12, false, company, M, 84);
  }

  // Date right-aligned, in line with the role headline.
  if (dateStr) {
    T(doc, COLOR.muted, 9, false, dateStr, W - M, 73, { align: 'right' });
  }
};

// ── Page header (page 2+) ──────────────────────────────────────────────
// Slim running head with a hairline rule below.
export const drawPageHeader = (doc, role) => {
  const { W, M } = PDF;
  T(doc, COLOR.ink,   8, true, 'TERVIEW', M, 12);
  T(doc, COLOR.muted, 7, false, '·  INTERVIEW PREPARATION', M + 17, 12);
  if (role) T(doc, COLOR.muted, 8, false, role, W - M, 12, { align: 'right' });
  hLine(doc, M, 15, W - M, COLOR.hairline[0], COLOR.hairline[1], COLOR.hairline[2], 0.3);
};

// ── Footer (every page) ───────────────────────────────────────────────
// Hairline rule + tiny captions. No filled bar.
export const drawFooters = (doc) => {
  const { W, H, M } = PDF;
  const n = doc.getNumberOfPages();
  for (let p = 1; p <= n; p++) {
    doc.setPage(p);
    hLine(doc, M, H - 14, W - M, COLOR.hairline[0], COLOR.hairline[1], COLOR.hairline[2], 0.3);
    T(doc, COLOR.muted, 7, false, 'terview.ai  ·  AI-powered interview preparation', M, H - 8);
    T(doc, COLOR.muted, 7, false, `${p} / ${n}`, W - M, H - 8, { align: 'right' });
  }
};

// ── Metadata block ────────────────────────────────────────────────────
// Two-column key/value list with hairline row dividers. Reads as a
// contents table in a serious report. No colored cards, no accent
// edges that could cross over the eyebrow text.
export const drawMetaCard = (doc, fields, startY) => {
  const { M, CW } = PDF;
  const validFields = fields.filter(f => f.value);
  const rowH = 8;
  const totalH = validFields.length * rowH;

  // Top rule
  hLine(doc, M, startY, M + CW, COLOR.hairline[0], COLOR.hairline[1], COLOR.hairline[2], 0.3);

  validFields.forEach(({ label, value }, idx) => {
    const ry = startY + idx * rowH;
    doc.setCharSpace(0.9);
    T(doc, COLOR.muted, 7, true, String(label).toUpperCase(), M, ry + 5.3);
    doc.setCharSpace(0);
    T(doc, COLOR.ink, 10, false, String(value), M + 52, ry + 5.3);
    hLine(doc, M, ry + rowH, M + CW, COLOR.hairline[0], COLOR.hairline[1], COLOR.hairline[2], 0.3);
  });

  return startY + totalH;
};

// ── Section heading ──────────────────────────────────────────────────
// Letter-spaced eyebrow, hairline rule below. Looks like a magazine
// section break.
export const drawSectionHeading = (doc, text, y) => {
  const { M, CW } = PDF;
  doc.setCharSpace(1.2);
  T(doc, COLOR.ink, 9, true, String(text).toUpperCase(), M, y);
  doc.setCharSpace(0);
  hLine(doc, M, y + 3, M + CW, COLOR.hairline[0], COLOR.hairline[1], COLOR.hairline[2], 0.3);
  return y + 10;
};
